import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { commentApi } from '../services/api';
import './CommentSection.css';

const CommentSection = ({ pageId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ name: '', email: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const { user } = useAuth();

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading comments for pageId:', pageId);
      console.log('API URL:', `${process.env.NODE_ENV === 'production' ? 'https://mohanhao.info/api' : '/api'}/comments/page/${pageId}`);
      
      const response = await commentApi.getByPage(pageId);
      console.log('Comments response:', response);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      setMessage('Error loading comments: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.name || !newComment.email || !newComment.content) {
      setMessage('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      setMessage('');
      
      const commentData = {
        ...newComment,
        pageId
      };
      
      console.log('Submitting comment:', commentData);
      console.log('API URL:', `${process.env.NODE_ENV === 'production' ? 'https://mohanhao.info/api' : '/api'}/comments`);
      
      const response = await commentApi.create(commentData);
      console.log('Comment submission response:', response);
      
      setNewComment({ name: '', email: '', content: '' });
      setMessage(response.data.message);
      
      // Reload comments after successful submission
      setTimeout(() => {
        loadComments();
      }, 1000);
      
    } catch (error) {
      console.error('Error submitting comment:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      setMessage(error.response?.data?.message || 'Error submitting comment: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewComment(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="comment-section">
      <h3>Comments</h3>
      
      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="comment-form">
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={newComment.name}
            onChange={handleInputChange}
            maxLength={100}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={newComment.email}
            onChange={handleInputChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="content">Comment *</label>
          <textarea
            id="content"
            name="content"
            value={newComment.content}
            onChange={handleInputChange}
            maxLength={2000}
            rows={4}
            required
          />
          <div className="char-count">
            {newComment.content.length}/2000 characters
          </div>
        </div>
        
        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
        
        <button type="submit" disabled={submitting} className="submit-button">
          {submitting ? 'Submitting...' : 'Submit Comment'}
        </button>
      </form>
      
      {/* Comments List */}
      <div className="comments-list">
        {loading ? (
          <div className="loading">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="no-comments">No comments yet. Be the first to comment!</div>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="comment">
              <div className="comment-header">
                <span className="comment-author">{comment.name}</span>
                <span className="comment-date">{formatDate(comment.createdAt)}</span>
              </div>
              <div className="comment-content">{comment.content}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection; 